import {
  normalizeMeetingMinutesResponse,
  type AnalyzeMeetingFilesResponse,
  type MeetingMinutesData,
  type PreviousAnalysis,
  type SummarizeMemoRequest,
  type SummarizeMemoResponse,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'
import { normalizeMemoResponse } from '@/utils/mapMemoToMeetingInsights'

export interface TranslateMeetingMinutesRequest {
  /** Target locale to translate the minutes into (`ja`, `vi`, `en`, …). */
  language: string
  /** Existing meeting minutes JSON to translate. */
  meeting_minutes: MeetingMinutesData
}

/**
 * Translate an existing meeting-minutes JSON into another language via the
 * backend-proxied AI service. Used when exporting DOCX in a language other
 * than the meeting's `targetLang`, since the stored `insights` are only
 * ever generated in that target language.
 */
export async function translateMeetingMinutes(
  payload: TranslateMeetingMinutesRequest
): Promise<MeetingMinutesData> {
  try {
    const { data } = await apiClient.post<unknown>('/api/ai/meeting/translate-minutes', payload)
    return normalizeMeetingMinutesResponse(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedTranslateMeetingMinutes)
  }
}

export async function summarizeMemo(payload: SummarizeMemoRequest): Promise<PreviousAnalysis> {
  try {
    const { data } = await apiClient.post<SummarizeMemoResponse>('/api/ai/summarize-memo', payload)
    return normalizeMemoResponse(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSummarizeMeetingMemo)
  }
}

export async function analyzeMeetingFiles(
  files: File[],
  meetingLanguage: string
): Promise<AnalyzeMeetingFilesResponse> {
  const formData = new FormData()
  formData.append('meeting_language', meetingLanguage)
  files.forEach((file) => formData.append('files', file))

  try {
    const { data } = await apiClient.post<AnalyzeMeetingFilesResponse>(
      '/api/ai/meeting/analyze-files',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )

    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedAnalyzeMeetingFiles)
  }
}
